import { ensureIndexedDbSpace } from "@Services/utils/modelPropertiesDataDB";

const DB_NAME = "nodeIdAndUniqueIdMapData";
const STORE = "nodeIdAndUniqueIdMap";
const DB_VERSION = 1;

export interface NodeIdUniqueIdFileEntry {
  /** Maps nodeId (string) → cubeUniqueId. Values are strings at runtime. */
  nodeIdMap: Record<string, string | number | boolean | null> | null;
  /** Inverse: cubeUniqueId (string) → nodeIds (number[]). */
  cubeUniqueIdMap: Record<string, number[]>;
}

type StoredRow = { fileVersionId: string } & NodeIdUniqueIdFileEntry;

const memoryCache = new Map<string, NodeIdUniqueIdFileEntry>();
let dbInstance: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "fileVersionId" });
      }
    };
  });
}

function buildCubeUniqueIdMap(data: Record<string, string | number | boolean | null> | null): Record<string, number[]> {
  const cubeUniqueIdMap: Record<string, number[]> = {};
  if (!data) return cubeUniqueIdMap;
  for (const nodeId in data) {
    const mapKey = data[nodeId] as string;
    if (!mapKey) continue;
    (cubeUniqueIdMap[mapKey] ??= []).push(Number(nodeId));
  }
  return cubeUniqueIdMap;
}

export async function setNodeIdAndUniqueIdMapForFile(
  fileVersionId: string,
  data: Record<string, string | number | boolean | null> | null,
): Promise<void> {
  const entry: NodeIdUniqueIdFileEntry = {
    nodeIdMap: data,
    cubeUniqueIdMap: buildCubeUniqueIdMap(data),
  };

  memoryCache.set(fileVersionId, entry);

  const row: StoredRow = { fileVersionId, ...entry };
  if (!(await ensureIndexedDbSpace(row))) return;
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function hasNodeIdAndUniqueIdMap(fileVersionId: string): Promise<boolean> {
  if (memoryCache.has(fileVersionId)) return true;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).getKey(fileVersionId);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result !== undefined);
  });
}

export async function loadNodeIdAndUniqueIdMapIntoCache(fileVersionId: string): Promise<void> {
  if (memoryCache.has(fileVersionId)) return;
  const db = await openDB();
  const row = await new Promise<StoredRow | undefined>((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(fileVersionId);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result as StoredRow | undefined);
  });
  if (row) {
    memoryCache.set(fileVersionId, { nodeIdMap: row.nodeIdMap, cubeUniqueIdMap: row.cubeUniqueIdMap });
  }
}

export function getNodeIdAndUniqueIdMapEntry(fileVersionId: string): NodeIdUniqueIdFileEntry | undefined {
  return memoryCache.get(fileVersionId);
}

export function getLoadedNodeIdAndUniqueIdFileVersionIds(): string[] {
  return [...memoryCache.keys()];
}

export function clearOtherNodeIdAndUniqueIdCacheMaps(keepFileVersionIds: string[]): void {
  const keep = new Set(keepFileVersionIds);
  for (const id of memoryCache.keys()) {
    if (!keep.has(id)) memoryCache.delete(id);
  }
}

export async function clearOtherNodeIdAndUniqueIdMaps(keepFileVersionIds: string[]): Promise<void> {
  const keep = new Set(keepFileVersionIds);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const objectStore = tx.objectStore(STORE);
    const cursorReq = objectStore.openKeyCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      if (!keep.has(cursor.key as string)) objectStore.delete(cursor.key);
      cursor.continue();
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
